/**
 * TimelineFilters - Filter controls for timeline events
 */

import React from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { useTranslation } from 'localization';
import { TimelineEventType, TimelineEventCategory, TimelineFilters as TimelineFiltersType } from 'types/timeline';
import './TimelineFiltersMolecule.scss';
import { ACTIVITY_EVENT_TYPE_CONFIGS } from '../../../config/activityEventTypes';

export interface TimelineFiltersProps {
  filters: TimelineFiltersType;
  onFiltersChange: (filters: TimelineFiltersType) => void;
  onReset: () => void;
}

/**
 * Every filterable event type, DERIVED from the shared config rather than hand-listed.
 *
 * The previous literal held 10 legacy PascalCase values — a stale subset that both omitted most
 * real types and offered several (ArcActivated / ArcCompleted / ArcCancelled / ArcFailed) that no
 * writer ever produced, so those filters could only ever return nothing. Deriving it means adding a
 * `LedgerEntryType` to the config surfaces it here automatically and it cannot drift again.
 */
const ALL_EVENT_TYPES: TimelineEventType[] = ACTIVITY_EVENT_TYPE_CONFIGS.map((c) => c.value);

const ALL_CATEGORIES: TimelineEventCategory[] = [
  'system',
  'workflow',
  'security',
  'userAction',
  'admin',
];

const TimelineFiltersMolecule: React.FC<TimelineFiltersProps> = ({
  filters,
  onFiltersChange,
  onReset,
}) => {
  const { t } = useTranslation();

  // Count active filters
  const activeFilterCount =
    filters.eventTypes.length +
    filters.categories.length +
    (filters.dateRange.start ? 1 : 0) +
    (filters.dateRange.end ? 1 : 0);

  const eventTypeOptions = ALL_EVENT_TYPES.map((type) => ({
    label: t(`timeline.eventTypes.${type}`),
    value: type,
  }));

  const categoryOptions = ALL_CATEGORIES.map((cat) => ({
    label: t(`timeline.categories.${cat}`),
    value: cat,
  }));

  const handleEventTypesChange = (selectedTypes: TimelineEventType[]) => {
    onFiltersChange({
      ...filters,
      eventTypes: selectedTypes,
    });
  };

  const handleCategoriesChange = (selectedCategories: TimelineEventCategory[]) => {
    onFiltersChange({
      ...filters,
      categories: selectedCategories,
    });
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: Date | null) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        [field]: value,
      },
    });
  };

  return (
    <div className="timeline-filters">
      <div className="timeline-filters__header">
        <h3 className="timeline-filters__title">
          {t('timeline.filters.title')}
          {activeFilterCount > 0 && (
            <Badge value={activeFilterCount} severity="info" className="timeline-filters__badge" />
          )}
        </h3>
        {activeFilterCount > 0 && (
          <Button
            label={t('timeline.filters.reset')}
            icon="pi pi-times"
            className="p-button-text p-button-sm"
            onClick={onReset}
          />
        )}
      </div>

      <div className="timeline-filters__controls">
        {/* Event Types Filter */}
        <div className="timeline-filters__control">
          <label className="timeline-filters__label">
            {t('timeline.filters.eventTypes')}
          </label>
          <MultiSelect
            value={filters.eventTypes}
            options={eventTypeOptions}
            onChange={(e) => handleEventTypesChange(e.value)}
            placeholder={t('timeline.filters.eventTypes')}
            display="chip"
            filter
            className="timeline-filters__multiselect"
          />
        </div>

        {/* Categories Filter */}
        <div className="timeline-filters__control">
          <label className="timeline-filters__label">
            {t('timeline.filters.categories')}
          </label>
          <MultiSelect
            value={filters.categories}
            options={categoryOptions}
            onChange={(e) => handleCategoriesChange(e.value)}
            placeholder={t('timeline.filters.categories')}
            display="chip"
            className="timeline-filters__multiselect"
          />
        </div>

        {/* Date Range Filter */}
        <div className="timeline-filters__control">
          <label className="timeline-filters__label">
            {t('timeline.filters.dateRange')}
          </label>
          <div className="timeline-filters__date-range">
            <Calendar
              value={filters.dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.value as Date | null)}
              placeholder={t('timeline.filters.startDate')}
              showIcon
              className="timeline-filters__calendar"
            />
            <span className="timeline-filters__date-separator">–</span>
            <Calendar
              value={filters.dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.value as Date | null)}
              placeholder={t('timeline.filters.endDate')}
              showIcon
              className="timeline-filters__calendar"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineFiltersMolecule;
